/*
 * Draft 13 in the click-through preview: agency mode at The Marketers, with made-up clients, deals, plans, tasks,
 * projects and results kept in memory, worked out by the same shared rules the server uses. Nothing is sent or charged.
 */
import {
  addDays,
  addMonths,
  agreementState,
  callSlides,
  clientHealth,
  costPerLead,
  dueWords,
  endsOnAfterNotice,
  leftIfStoppedEarly,
  lifecycleOf,
  ONBOARDING_TASKS,
  retainerMonthsDue,
  termEndsOn,
  type AgencyClientDetail,
  type AgencyClientRow,
  type AgencyHome,
  type AgreementRow,
  type CallFacts,
  type ClientResults,
  type DeckRow,
  type ProjectRow,
  type ProjectStatus,
  type ReportRow,
  type TaskKind,
  type TaskRow,
} from "@steward/shared";
import { addAgencyLead, agencyLeads, useAgencyStages } from "./money";

type Answer = Promise<{ status: number; body: unknown }>;
type Json = Record<string, unknown>;
const ok = (body: unknown, status = 200): Answer => Promise.resolve({ status, body });
const fail = (status: number, code: string, message: string): Answer => Promise.resolve({ status, body: { code, message } });
const NO = () => fail(403, "not_allowed", "Your role can't do that. Ask the owner if you need it.");
const str = (v: unknown) => (typeof v === "string" ? v : "");
let n = 0;
const id = () => `00000000-0000-4000-8000-${String(950000000000 + (n += 1))}`;
const TZ = "America/Chicago";
const today = () => new Date().toLocaleDateString("en-CA", { timeZone: TZ });

interface Client {
  id: string;
  name: string;
  contact: string | null;
  email: string | null;
  phone: string | null;
  ownerId: string | null;
  linkedCompanyId: string | null;
  linkedName: string | null;
  /** Made-up live numbers for linked clients, and typed months for the rest. */
  live: {
    leadsNow: number;
    leadsBefore: number;
    spendCents: number;
    reviews: number;
    rating: number;
    rankNow: number;
    rankBefore: number;
  } | null;
  lastActivityDays: number;
}
interface Plan {
  id: string;
  clientId: string;
  quoteNumber: number;
  monthlyCents: number;
  setupCents: number;
  startOn: string;
  noticeOn: string | null;
  autoPay: boolean;
  signedBy: string;
}
interface Task {
  id: string;
  title: string;
  kind: TaskKind;
  dueOn: string;
  dueTime: string | null;
  assignedTo: string | null;
  clientId: string | null;
  projectId: string | null;
  leadId: string | null;
  group: string | null;
  doneAt: string | null;
}
interface Project {
  id: string;
  number: number;
  clientId: string;
  name: string;
  kind: "retainer" | "project";
  status: ProjectStatus;
  ownerId: string | null;
  planId: string | null;
  startOn: string | null;
  dueOn: string | null;
}

const st = {
  companyId: "",
  team: [] as { id: string; name: string }[],
  linkable: [] as { id: string; name: string }[],
  clients: [] as Client[],
  plans: [] as Plan[],
  tasks: [] as Task[],
  projects: [] as Project[],
  reports: [] as ReportRow[],
  decks: [] as DeckRow[],
};

/** Made-up agency data for the preview. Our own locations show as test clients (owner, 2026-10-22). */
export function seedAgency(opts: {
  companyId: string;
  team: { id: string; name: string }[];
  ours: { id: string; name: string }[];
}): void {
  st.companyId = opts.companyId;
  st.team = opts.team;
  st.linkable = opts.ours;
  useAgencyStages(opts.companyId);
  const t = today();
  const owner = opts.team[0]?.id ?? null;
  const helper = opts.team[1]?.id ?? owner;
  const client = (c: Omit<Client, "id">) => {
    const x = { ...c, id: id() };
    st.clients.push(x);
    return x.id;
  };
  const ourLive = [
    { leadsNow: 41, leadsBefore: 36, spendCents: 182_000, reviews: 412, rating: 4.9, rankNow: 2.4, rankBefore: 3.1 },
    { leadsNow: 18, leadsBefore: 22, spendCents: 96_000, reviews: 168, rating: 4.8, rankNow: 4.2, rankBefore: 3.9 },
    { leadsNow: 27, leadsBefore: 25, spendCents: 120_000, reviews: 233, rating: 4.9, rankNow: 3.0, rankBefore: 3.6 },
    { leadsNow: 9, leadsBefore: 15, spendCents: 70_000, reviews: 88, rating: 4.7, rankNow: 7.8, rankBefore: 4.6 },
  ];
  opts.ours.slice(0, 4).forEach((o, i) =>
    client({
      name: o.name,
      contact: null,
      email: null,
      phone: null,
      ownerId: owner,
      linkedCompanyId: o.id,
      linkedName: o.name,
      live: ourLive[i]!,
      lastActivityDays: i + 1,
    }),
  );
  const bayou = client({
    name: "Bayou Plumbing",
    contact: "Ann Lee",
    email: "ann@example.com",
    phone: "(504) 555-0181",
    ownerId: owner,
    linkedCompanyId: null,
    linkedName: null,
    live: null,
    lastActivityDays: 0,
  });
  const gulf = client({
    name: "Gulf Coast Roofing",
    contact: "Ray Dupre",
    email: "ray@example.com",
    phone: "(504) 555-0192",
    ownerId: helper,
    linkedCompanyId: null,
    linkedName: null,
    live: null,
    lastActivityDays: 3,
  });
  const crescent = client({
    name: "Crescent Pest Control",
    contact: "Tia Nguyen",
    email: "tia@example.com",
    phone: "(504) 555-0173",
    ownerId: owner,
    linkedCompanyId: null,
    linkedName: null,
    live: null,
    lastActivityDays: 1,
  });
  const plan = (p: Omit<Plan, "id">) => {
    const x = { ...p, id: id() };
    st.plans.push(x);
    const proj: Project = {
      id: id(),
      number: st.projects.length + 101,
      clientId: p.clientId,
      name: "Monthly marketing",
      kind: "retainer",
      status: addDays(p.startOn, 14) > t ? "onboarding" : "active",
      ownerId: owner,
      planId: x.id,
      startOn: p.startOn,
      dueOn: null,
    };
    st.projects.push(proj);
    ONBOARDING_TASKS.forEach((o, i) =>
      st.tasks.push({
        id: id(),
        title: o.title,
        kind: o.kind,
        dueOn: addDays(p.startOn, o.days),
        dueTime: o.kind === "meeting" ? "10:00" : null,
        assignedTo: i % 2 ? helper : owner,
        clientId: p.clientId,
        projectId: proj.id,
        leadId: null,
        group: "onboarding",
        doneAt: addDays(p.startOn, o.days) < addDays(t, -2) ? addDays(p.startOn, o.days) : null,
      }),
    );
  };
  plan({
    clientId: bayou,
    quoteNumber: 1012,
    monthlyCents: 150_000,
    setupCents: 50_000,
    startOn: addDays(t, -6),
    noticeOn: null,
    autoPay: true,
    signedBy: "Ann Lee",
  });
  plan({
    clientId: gulf,
    quoteNumber: 1004,
    monthlyCents: 200_000,
    setupCents: 75_000,
    startOn: addMonths(t, -5),
    noticeOn: addDays(t, -4),
    autoPay: false,
    signedBy: "Ray Dupre",
  });
  st.projects.push({
    id: id(),
    number: 110,
    clientId: gulf,
    name: "New website",
    kind: "project",
    status: "active",
    ownerId: helper,
    planId: null,
    startOn: addDays(t, -20),
    dueOn: addDays(t, 12),
  });
  const task = (title: string, kind: TaskKind, days: number, time: string | null, clientId: string | null, who: string | null) =>
    st.tasks.push({
      id: id(),
      title,
      kind,
      dueOn: addDays(t, days),
      dueTime: time,
      assignedTo: who,
      clientId,
      projectId: null,
      leadId: null,
      group: null,
      doneAt: null,
    });
  task("Send October report", "email", -1, null, gulf, helper);
  task("Call Tia about the proposal", "call", 0, "14:00", crescent, owner);
  task("Check new ad pictures", "todo", 0, null, bayou, helper);
  task("Monthly call with Ray", "meeting", 2, "09:30", gulf, owner);
  // Typed months for Gulf Coast Roofing (not one of ours).
  const months = [
    [6, 12, 410_000, 61, 4.6, 6.2],
    [5, 14, 400_000, 66, 4.6, 5.8],
    [4, 17, 420_000, 70, 4.7, 5.1],
    [3, 16, 405_000, 74, 4.7, 4.9],
    [2, 19, 415_000, 79, 4.7, 4.4],
    [1, 13, 390_000, 81, 4.7, 4.8],
  ] as const;
  for (const [ago, leads, spend, reviews, rating, rank] of months)
    st.reports.push({
      id: id(),
      clientId: gulf,
      month: addMonths(`${t.slice(0, 7)}-01`, -ago).slice(0, 7),
      numbers: { leads, spendCents: spend, reviews, rating, rankAverage: rank, collectedCents: null },
      source: "typed",
      health: "none",
      note: null,
      createdAt: new Date().toISOString(),
    });
  // Deals on the board.
  const lead = (
    name: string,
    business: string,
    stage: string,
    valueCents: number,
    service: string,
    daysAgo: number,
    extra: Json = {},
  ) =>
    addAgencyLead({
      companyId: opts.companyId,
      name,
      businessName: business,
      phone: null,
      email: `${name.split(" ")[0]!.toLowerCase()}@example.com`,
      address: "Metairie, LA",
      service,
      valueCents,
      source: "Website",
      clientId: null,
      quoteId: null,
      note: null,
      website: `${business.toLowerCase().replace(/[^a-z]+/g, "")}.example.com`,
      assignedTo: owner,
      stage,
      daysAgo,
      ...extra,
    });
  lead("Lou Marchand", "Magnolia HVAC", "New lead", 120_000, "air conditioning", 0);
  lead("Dee Fontenot", "Big Easy Electric", "Discovery call", 150_000, "electrical work", 3);
  lead("Tia Nguyen", "Crescent Pest Control", "Proposal sent", 180_000, "pest control", 5, { clientId: crescent });
  lead("Sam Boudreaux", "Delta Landscaping", "Negotiating", 100_000, "landscaping", 8);
  lead("Ann Lee", "Bayou Plumbing", "Won", 150_000, "plumbing", 6, { clientId: bayou });
  lead("Ray Dupre", "Gulf Coast Roofing", "Won", 200_000, "roofing", 150, { clientId: gulf });
}

// Rows -----------------------------------------------------------------------------------------------------------------

const nameOf = (uid: string | null) => st.team.find((p) => p.id === uid)?.name ?? null;

function planRow(p: Plan): AgreementRow {
  const t = today();
  const d = { startOn: p.startOn, termMonths: 3, noticeDays: 30, noticeOn: p.noticeOn };
  const state = agreementState(d, t);
  const billed = retainerMonthsDue(d, t).length;
  return {
    id: p.id,
    clientId: p.clientId,
    clientName: st.clients.find((c) => c.id === p.clientId)?.name ?? "",
    quoteId: p.id,
    quoteNumber: p.quoteNumber,
    projectId: st.projects.find((x) => x.planId === p.id)?.id ?? null,
    monthlyCents: p.monthlyCents,
    setupCents: p.setupCents,
    startOn: p.startOn,
    termEndsOn: termEndsOn(d),
    noticeOn: p.noticeOn,
    endsOn: endsOnAfterNotice(d),
    state,
    autoPay: p.autoPay,
    signedBy: p.signedBy,
    signedAt: `${p.startOn}T15:00:00.000Z`,
    billedMonths: 1 + billed,
    nextBillOn: state === "ended" ? null : (retainerMonthsDue(d, "9999-12-31").find((m) => m.billOn > t)?.billOn ?? null),
    leftIfStoppedCents: state === "ended" ? 0 : leftIfStoppedEarly({ ...d, monthlyCents: p.monthlyCents }, t),
    firstInvoiceId: null,
  };
}

function taskRow(x: Task): TaskRow {
  const t = today();
  const c = st.clients.find((y) => y.id === x.clientId);
  return {
    id: x.id,
    title: x.title,
    note: null,
    kind: x.kind,
    dueOn: x.dueOn,
    dueTime: x.dueTime,
    dueWords: dueWords(x.dueOn, x.dueTime, TZ),
    overdue: !x.doneAt && x.dueOn < t,
    assignedTo: x.assignedTo,
    assignedName: nameOf(x.assignedTo),
    clientId: x.clientId,
    clientName: c?.name ?? null,
    leadId: x.leadId,
    leadName: null,
    projectId: x.projectId,
    projectName: st.projects.find((p) => p.id === x.projectId)?.name ?? null,
    group: x.group,
    doneAt: x.doneAt,
  };
}

function projectRow(p: Project): ProjectRow {
  const mine = st.tasks.filter((x) => x.projectId === p.id);
  return {
    id: p.id,
    number: p.number,
    clientId: p.clientId,
    clientName: st.clients.find((c) => c.id === p.clientId)?.name ?? "",
    name: p.name,
    kind: p.kind,
    status: p.status,
    ownerId: p.ownerId,
    ownerName: nameOf(p.ownerId),
    startOn: p.startOn,
    dueOn: p.dueOn,
    notes: null,
    monthlyCents: st.plans.find((x) => x.id === p.planId)?.monthlyCents ?? null,
    agreementId: p.planId,
    openTasks: mine.filter((x) => !x.doneAt).length,
    doneTasks: mine.filter((x) => x.doneAt).length,
  };
}

function results(c: Client): ClientResults {
  const t = today();
  const live = planRows(c.id).filter((p) => p.state !== "ended");
  const base = {
    clientId: c.id,
    name: c.name,
    retainerCents: live.reduce((s, p) => s + p.monthlyCents, 0),
    nextTask:
      st.tasks.filter((x) => x.clientId === c.id && !x.doneAt).sort((a, b) => a.dueOn.localeCompare(b.dueOn))[0]?.title ?? null,
    noAccess: false,
  };
  const six = Array.from({ length: 6 }, (_, i) => addMonths(`${t.slice(0, 7)}-01`, i - 5).slice(0, 7));
  if (c.live) {
    const l = c.live;
    return {
      ...base,
      source: "linked",
      sourceName: c.linkedName,
      health: clientHealth(l),
      ...l,
      costPerLeadCents: costPerLead(l.spendCents, l.leadsNow),
      collectedCents: null,
      months: six.map((m, i) => ({ month: m, leads: Math.round(l.leadsBefore * (0.7 + i * 0.07)), rank: null })),
    };
  }
  const mine = st.reports.filter((r) => r.clientId === c.id).sort((a, b) => b.month.localeCompare(a.month));
  const [now, before] = mine;
  if (!now)
    return {
      ...base,
      source: "none",
      sourceName: null,
      health: "none",
      leadsNow: null,
      leadsBefore: null,
      spendCents: null,
      costPerLeadCents: null,
      reviews: null,
      rating: null,
      rankNow: null,
      rankBefore: null,
      collectedCents: null,
      months: [],
    };
  const x = now.numbers;
  return {
    ...base,
    source: "typed",
    sourceName: null,
    health: clientHealth({
      leadsNow: x.leads,
      leadsBefore: before?.numbers.leads ?? null,
      rankNow: x.rankAverage,
      rankBefore: before?.numbers.rankAverage ?? null,
    }),
    leadsNow: x.leads,
    leadsBefore: before?.numbers.leads ?? null,
    spendCents: x.spendCents,
    costPerLeadCents: x.spendCents !== null && x.leads !== null ? costPerLead(x.spendCents, x.leads) : null,
    reviews: x.reviews,
    rating: x.rating,
    rankNow: x.rankAverage,
    rankBefore: before?.numbers.rankAverage ?? null,
    collectedCents: x.collectedCents,
    months: six.map((m) => {
      const r = mine.find((y) => y.month === m);
      return { month: m, leads: r?.numbers.leads ?? null, rank: r?.numbers.rankAverage ?? null };
    }),
  };
}

const planRows = (clientId?: string) => st.plans.filter((p) => !clientId || p.clientId === clientId).map(planRow);

function clientRow(c: Client): AgencyClientRow {
  const plans = planRows(c.id);
  const live = plans.filter((p) => p.state !== "ended");
  const { stages, leads } = agencyLeads(st.companyId);
  const open = stages.filter((s) => !s.fixed).map((s) => s.id);
  return {
    id: c.id,
    name: c.name,
    contact: c.contact,
    email: c.email,
    phone: c.phone,
    ownerId: c.ownerId,
    ownerName: nameOf(c.ownerId),
    retainerCents: live.reduce((s, p) => s + p.monthlyCents, 0),
    lifecycle: c.live
      ? "client"
      : lifecycleOf({
          livePlans: live.length,
          endedPlans: plans.length - live.length,
          openDeals: leads.filter((l) => l.clientId === c.id && open.includes(l.stageId)).length,
        }),
    lastActivityAt: new Date(Date.now() - c.lastActivityDays * 86_400_000).toISOString(),
    openTasks: st.tasks.filter((x) => x.clientId === c.id && !x.doneAt).length,
    health: results(c).health,
    linkedCompanyId: c.linkedCompanyId,
  };
}

function facts(leadId: string, paid: boolean): CallFacts | null {
  const l = agencyLeads(st.companyId).leads.find((x) => x.id === leadId);
  if (!l) return null;
  return {
    business: l.businessName ?? l.name,
    contact: l.businessName ? l.name : null,
    service: l.service,
    area: l.address,
    website: l.website ? `https://${l.website}` : null,
    site: l.website ? { loads: true, phoneShown: false, hasForm: true } : null,
    profile: { rating: 4.4, reviewCount: 31 },
    mapRank: null,
    competitors: paid
      ? [
          { name: "Southern Comfort Services", rating: null, reviewCount: 214 },
          { name: "Lakeside Pros", rating: null, reviewCount: 97 },
          { name: "Westbank Experts", rating: null, reviewCount: 22 },
        ]
      : [],
    offer: [],
    agency: "The Marketers",
  };
}

// Routes ---------------------------------------------------------------------------------------------------------------

/** The agency routes, or null when the path is not one of them. */
export function agencyRoute(
  gate: (a: Parameters<typeof import("@steward/shared").can>[1]) => boolean,
  c: { id: string; trade: string },
  me: string,
  method: string,
  p: string,
  q: URLSearchParams,
  b: Json,
  makeAccount: (name: string) => { id: string; name: string },
): Answer | null {
  if (!p.startsWith("agency/")) return null;
  if (c.trade !== "agency" || c.id !== st.companyId)
    return fail(404, "not_agency", "This page is only for the marketing agency.");
  const seg = p.split("/");
  const route = `${method} ${p}`;
  const t = today();
  const findClient = (x: string) => st.clients.find((y) => y.id === x);

  if (route === "GET agency/home") {
    if (!gate("company.read")) return NO();
    const plans = planRows();
    const { stages, leads } = agencyLeads(st.companyId);
    const live = plans.filter((x) => x.state !== "ended");
    const body: AgencyHome = {
      timezone: TZ,
      mrrCents: live.reduce((s, x) => s + x.monthlyCents, 0),
      livePlans: live.length,
      onNotice: live.filter((x) => x.state === "notice").length,
      stages: stages
        .filter((s) => !s.fixed)
        .map((s) => {
          const here = leads.filter((l) => l.stageId === s.id);
          return {
            stageId: s.id,
            name: s.name,
            count: here.length,
            valueCents: here.reduce((a, l) => a + (l.valueCents ?? 0), 0),
          };
        }),
      tasksDue: st.tasks.filter((x) => !x.doneAt && x.dueOn <= t).map(taskRow),
      overdue: st.tasks.filter((x) => !x.doneAt && x.dueOn < t).length,
      unpaid: { count: 2, cents: 275_000 },
      rankMoves: st.clients
        .map(results)
        .filter((r) => r.rankNow !== null && r.rankBefore !== null)
        .map((r) => ({ clientId: r.clientId, name: r.name, rankNow: r.rankNow!, rankBefore: r.rankBefore!, health: r.health })),
    };
    return ok(body);
  }
  if (route === "GET agency/clients") return gate("client.read") ? ok(st.clients.map(clientRow)) : NO();
  if (route === "GET agency/results")
    return gate("client.read")
      ? ok(st.clients.filter((x) => x.live !== null || st.plans.some((pl) => pl.clientId === x.id)).map(results))
      : NO();
  if (route === "GET agency/plans") return gate("invoice.read") ? ok(planRows()) : NO();
  if (route === "GET agency/team") return ok(st.team);
  if (route === "GET agency/projects")
    return gate("job.read") ? ok(st.projects.map(projectRow).sort((a, x) => x.number - a.number)) : NO();
  if (route === "GET agency/tasks") {
    if (!gate("client.read")) return NO();
    const view = q.get("view") ?? "open";
    const clientId = q.get("clientId");
    const rows = st.tasks
      .filter((x) => !clientId || x.clientId === clientId)
      .filter((x) =>
        view === "done"
          ? x.doneAt
          : !x.doneAt &&
            (view === "today" ? x.dueOn <= t : view === "overdue" ? x.dueOn < t : view === "mine" ? x.assignedTo === me : true),
      )
      .sort((a, x) => a.dueOn.localeCompare(x.dueOn) || (a.dueTime ?? "").localeCompare(x.dueTime ?? ""));
    return ok(rows.map(taskRow));
  }
  if (route === "POST agency/tasks") {
    if (!gate("client.update")) return NO();
    if (!str(b.title).trim()) return fail(400, "bad_request", "Check title. Say what needs doing.");
    const x: Task = {
      id: id(),
      title: str(b.title),
      kind: (str(b.kind) || "todo") as TaskKind,
      dueOn: str(b.dueOn) || t,
      dueTime: str(b.dueTime) || null,
      assignedTo: str(b.assignedTo) || null,
      clientId: str(b.clientId) || null,
      projectId: str(b.projectId) || null,
      leadId: str(b.leadId) || null,
      group: null,
      doneAt: null,
    };
    st.tasks.push(x);
    return ok({ id: x.id }, 201);
  }
  if (method === "PATCH" && seg[1] === "tasks" && seg[2]) {
    if (!gate("client.update")) return NO();
    const x = st.tasks.find((y) => y.id === seg[2]);
    if (!x) return fail(404, "not_found", "That task was not found.");
    if (b.done === true) x.doneAt = new Date().toISOString();
    if (b.done === false) x.doneAt = null;
    if (typeof b.title === "string") x.title = b.title;
    return ok({ ok: true });
  }
  if (route === "POST agency/projects") {
    if (!gate("job.update")) return NO();
    if (!findClient(str(b.clientId))) return fail(404, "not_found", "That client was not found.");
    const x: Project = {
      id: id(),
      number: Math.max(100, ...st.projects.map((y) => y.number)) + 1,
      clientId: str(b.clientId),
      name: str(b.name),
      kind: "project",
      status: "active",
      ownerId: null,
      planId: null,
      startOn: t,
      dueOn: str(b.dueOn) || null,
    };
    st.projects.push(x);
    return ok({ id: x.id }, 201);
  }
  if (method === "PATCH" && seg[1] === "projects" && seg[2]) {
    if (!gate("job.update")) return NO();
    const x = st.projects.find((y) => y.id === seg[2]);
    if (!x) return fail(404, "not_found", "That project was not found.");
    if (typeof b.status === "string") x.status = b.status as ProjectStatus;
    return ok({ ok: true });
  }
  if (seg[1] === "plans" && seg[3] === "notice") {
    if (!gate("invoice.update")) return NO();
    const x = st.plans.find((y) => y.id === seg[2]);
    if (!x) return fail(404, "not_found", "That plan was not found.");
    x.noticeOn = method === "DELETE" ? null : str(b.noticeOn) || t;
    return ok(planRow(x));
  }
  if (seg[1] === "clients" && seg[2]) {
    const c2 = findClient(seg[2]);
    if (!c2) return fail(404, "not_found", "That client was not found.");
    if (method === "GET" && seg.length === 3) {
      if (!gate("client.read")) return NO();
      const { stages, leads } = agencyLeads(st.companyId);
      const body: AgencyClientDetail = {
        client: clientRow(c2),
        agreements: planRows(c2.id),
        projects: st.projects.filter((x) => x.clientId === c2.id).map(projectRow),
        tasks: st.tasks.filter((x) => x.clientId === c2.id && !x.doneAt).map(taskRow),
        deals: leads
          .filter((l) => l.clientId === c2.id)
          .map((l) => ({
            id: l.id,
            name: l.businessName ?? l.name,
            stage: stages.find((s) => s.id === l.stageId)?.name ?? "",
            valueCents: l.valueCents,
          })),
        linkable: gate("company.update") ? st.linkable : [],
        account: (() => {
          const own = st.linkable.find((x) => x.id === c2.linkedCompanyId);
          if (own) return { ...own, own: true };
          return c2.linkedCompanyId && c2.linkedName ? { id: c2.linkedCompanyId, name: c2.linkedName, own: false } : null;
        })(),
      };
      return ok(body);
    }
    if (method === "GET" && seg[3] === "results")
      return ok({ results: results(c2), reports: st.reports.filter((r) => r.clientId === c2.id) });
    if (method === "PUT" && seg[3] === "owner") {
      if (!gate("client.update")) return NO();
      c2.ownerId = str(b.ownerId) || null;
      return ok({ ok: true });
    }
    if (method === "POST" && seg[3] === "account") {
      if (!gate("company.create")) return NO();
      if (c2.linkedCompanyId) return fail(409, "already_linked", "This client already has an account linked. Unlink it first.");
      const made = makeAccount(str(b.name) || c2.name);
      c2.linkedCompanyId = made.id;
      c2.linkedName = made.name;
      return ok({ id: made.id, name: made.name }, 201);
    }
    if (method === "PUT" && seg[3] === "link") {
      if (!gate("company.update")) return NO();
      const target = st.linkable.find((x) => x.id === str(b.companyId));
      if (b.companyId && !target) return fail(403, "cannot_link", "You can only link one of our own locations that you can see.");
      c2.linkedCompanyId = target?.id ?? null;
      c2.linkedName = target?.name ?? null;
      c2.live = target
        ? { leadsNow: 14, leadsBefore: 12, spendCents: 60_000, reviews: 120, rating: 4.8, rankNow: 3.4, rankBefore: 3.8 }
        : null;
      return ok({ ok: true });
    }
    if (method === "POST" && seg[3] === "reports") {
      if (!gate("client.update")) return NO();
      const month = str(b.month);
      const num = (k: string) => (typeof b[k] === "number" ? b[k] : null);
      const row: ReportRow = {
        id: id(),
        clientId: c2.id,
        month,
        numbers: {
          leads: num("leads"),
          spendCents: num("spendCents"),
          reviews: num("reviews"),
          rating: num("rating"),
          rankAverage: num("rankAverage"),
          collectedCents: num("collectedCents"),
        },
        source: "typed",
        health: "none",
        note: null,
        createdAt: new Date().toISOString(),
      };
      st.reports = st.reports.filter((r) => !(r.clientId === c2.id && r.month === month)).concat(row);
      return ok(row);
    }
  }
  if (seg[1] === "deals" && seg[2] && seg[3] === "slides") {
    if (method === "GET") return ok(st.decks.filter((d) => d.leadId === seg[2]));
    if (!gate("lead.update")) return NO();
    const f = facts(seg[2], b.lookUpCompetitors === true);
    if (!f) return fail(404, "not_found", "That deal was not found.");
    const deck: DeckRow = {
      id: id(),
      leadId: seg[2],
      slides: callSlides(f),
      paidChecks: b.lookUpCompetitors === true,
      createdAt: new Date().toISOString(),
    };
    st.decks.unshift(deck);
    return ok(deck, 201);
  }
  return null;
}
